import React, { useState, useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import { merchantSettlementPaid } from "../../../config/LiveApi";
import { toast } from "react-toastify";
import ConfirmModal from "../../../components/common/ConfirmModal";

const Modal = ({ onClose, reportData, refreshTableData, type }) => {
  const token = useSelector((store) => store.userReducer.token);

  // ✅ Detect Settlement or Rolling
  const isSettlement = !!reportData?.settlementTimeRange;

  // ✅ Correct available amount calculation
  const availableAmount = useMemo(() => {
    if (isSettlement) {
      return (
        (Number(reportData?.settlementAmount) || 0) -
        (Number(reportData?.paidAmount) || 0)
      );
    } else {
      return Number(reportData?.rrAmount) || 0;
    }
  }, [reportData, isSettlement]);

  // ✅ Default amount for direct pay
  const [amount, setAmount] = useState(
    type === "direct" ? availableAmount : "",
  );

  const [remark, setRemark] = useState("");
  const [finalRemark, setFinalRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    const numericAmount = Number(amount);

    if (
      !numericAmount ||
      isNaN(numericAmount) ||
      (+availableAmount > 0 && numericAmount <= 0) ||
      (+availableAmount < 0 && numericAmount >= 0)
    ) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (Math.abs(+numericAmount) > Math.abs(+availableAmount)) {
      toast.error("Amount exceeds available amount");
      return;
    }

    let prefix = isSettlement ? "SETTLEMENT" : "ROLLING";
    let generatedRemark = "";

    // ✅ Direct Pay → Auto remark with IST time
    if (type === "direct") {
      const currentDate = new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });

      generatedRemark = `${prefix} DIRECT PAY | ${currentDate}`;
    } else {
      // ✅ Manual Pay
      generatedRemark = `${prefix}${remark ? `, ${remark}` : ""}`;
    }

    setFinalRemark(generatedRemark);
    setShowConfirm(true);
  };

  const handleConfirm = async (confirmed) => {
    setShowConfirm(false);
    if (!confirmed) return;

    setLoading(true);

    try {
      await merchantSettlementPaid(
        token,
        reportData?.id,
        Number(amount),
        finalRemark,
      );

      toast.success("Amount paid successfully!");
      refreshTableData();
      onClose();
    } catch (err) {
      toast.error("Failed to update settlement amount");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="merchant-amount-modal-overlay">
        <div className="merchant-amount-modal-content">
          <button
            onClick={onClose}
            className="merchant-amount-modal-buttons-close"
          >
            <FaTimes size={25} />
          </button>

          <h2>
            Pay {reportData?.name || reportData?.alias} -{" "}
            {isSettlement ? "Settlement" : "Rolling Reserve"} Amount
          </h2>

          <p>
            Total {isSettlement ? "Settlement" : "Rolling Reserve"}: ₹{" "}
            {isSettlement
              ? Number(reportData?.settlementAmount || 0).toFixed(2)
              : Number(reportData?.rrAmount || 0).toFixed(2)}
          </p>

          <p>Available Amount: ₹ {Number(availableAmount).toFixed(2)}</p>

          <form onSubmit={handleSubmit}>
            <label htmlFor="amountInput">Enter Amount:</label>

            <input
              id="amountInput"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter Amount"
              required
              disabled={type === "direct"}
            />

            {/* Hide remark input for Direct Pay */}
            {type !== "direct" && (
              <>
                <label htmlFor="remarkID">Enter Remark:</label>
                <input
                  id="remarkID"
                  type="text"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Enter Remark"
                  required
                />
              </>
            )}

            <button
              className="amount-modal-buttons"
              type="submit"
              disabled={loading}
            >
              {loading ? "Paying..." : "Submit"}
            </button>
          </form>
        </div>
      </div>

      {showConfirm && <ConfirmModal onResolve={handleConfirm} />}
    </>
  );
};

export default Modal;
